/**
 * Period close — `/finance/period-close`.
 *
 * The checklist is topped up from the company's active templates on every
 * read, so a control added on the 29th appears on this month's list. The
 * screen therefore never caches a "frozen" checklist: it reads, and re-reads
 * after every decision.
 *
 * `skipped` is NOT a pass. It says why a check could not run and it keeps
 * blocking, so it is rendered as a blocker with its reason, never as a tick.
 * `evidence_row_count` is the exact number of offending rows even when the
 * stored sample is truncated, so the screen shows both — "5 shown of 812" is
 * the difference between a nuisance and an incident.
 *
 * Sign-off and waiver are different facts. An automated task cannot be signed
 * off at all — it is answered by its check — and a waiver demands a reason in
 * writing, because without one the record would say the close happened and
 * give nobody a way to find out why it was allowed to.
 */
import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Button, Card, CardContent, CardHeader, CardTitle } from "@erp/ui";
import { RefreshCw } from "lucide-react";

import { surfaceProblem } from "@/components/form";
import { MoneyDisplay } from "@/components/money/money-display";
import { useIdempotentMutation } from "@/features/shared/ops-api";

import {
  PERIOD_REOPEN_REASONS,
  decideCloseTask,
  executeYearEndClose,
  reopenFiscalPeriod,
  runPeriodCloseChecks,
  transitionFiscalPeriodClose,
  type ClosePeriod,
  type ClosePeriodStatus,
  type PeriodCloseChecklist,
  type PeriodCloseTask,
  type PeriodReopen,
  type YearEndClose,
} from "../api";
import {
  NoticeBanner,
  PageHeader,
  ProblemBanner,
  SelectField,
  StatusBadge,
  TextField,
} from "../components/fields";
import {
  financeKeys,
  useFiscalPeriods,
  useFiscalYears,
  usePeriodCloseChecklist,
  useYearEndClose,
} from "../queries";

const NONE = "__none__";

const TASK_STATUS_VARIANTS = {
  open: "outline",
  passed: "success",
  failed: "destructive",
  signed_off: "default",
  skipped: "warning",
  waived: "secondary",
} as const;

/** Forward steps only; reopening is a different permission on a different route. */
const FORWARD_TARGETS: Record<string, ClosePeriodStatus | undefined> = {
  open: "closing",
  closing: "closed",
};

export interface PeriodClosePageProps {
  canRunChecks?: boolean;
  canSignOff?: boolean;
  canTransition?: boolean;
  canReopen?: boolean;
  canReadYearEnd?: boolean;
  canExecuteYearEnd?: boolean;
  canReadFiscalPeriods?: boolean;
  canReadFiscalYears?: boolean;
}

export function PeriodClosePage({
  canRunChecks = true,
  canSignOff = true,
  canTransition = true,
  canReopen = true,
  canReadYearEnd = true,
  canExecuteYearEnd = true,
  canReadFiscalPeriods = true,
  canReadFiscalYears = true,
}: PeriodClosePageProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const periodsQuery = useFiscalPeriods(canReadFiscalPeriods);
  const yearsQuery = useFiscalYears(canReadFiscalYears);

  const [fiscalPeriodId, setFiscalPeriodId] = useState<string>(NONE);
  const [fiscalYearId, setFiscalYearId] = useState<string>(NONE);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [reopenReason, setReopenReason] =
    useState<PeriodReopen["reason_code"]>("authorized_reopen");
  const [formError, setFormError] = useState<string | null>(null);
  const [yearEndResult, setYearEndResult] = useState<{
    status: number;
    close: YearEndClose | null;
  } | null>(null);

  const checklistQuery = usePeriodCloseChecklist(fiscalPeriodId === NONE ? null : fiscalPeriodId);
  const yearEndQuery = useYearEndClose(fiscalYearId === NONE ? null : fiscalYearId, canReadYearEnd);

  const checklist = checklistQuery.data;

  const periodOptions = useMemo(
    () => [
      { value: NONE, label: t("finance.common.choose", "Choose…") },
      ...(periodsQuery.data ?? []).map((period) => ({
        value: period.id,
        label: `${period.name} (${period.starts_on} → ${period.ends_on})`,
      })),
    ],
    [periodsQuery.data, t],
  );

  const yearOptions = useMemo(
    () => [
      { value: NONE, label: t("finance.common.choose", "Choose…") },
      ...(yearsQuery.data ?? []).map((year) => ({ value: year.id, label: year.name })),
    ],
    [yearsQuery.data, t],
  );

  const onChecklistChanged = (next: PeriodCloseChecklist) => {
    queryClient.setQueryData(financeKeys.periodClose.checklist(next.fiscal_period_id), next);
  };

  const runChecksMutation = useIdempotentMutation<PeriodCloseChecklist, { fiscalPeriodId: string }>(
    {
      mutationFn: (input, key) => runPeriodCloseChecks(input.fiscalPeriodId, key),
      onSuccess: onChecklistChanged,
      onError: (error) => surfaceProblem(error, t),
    },
  );

  const decideMutation = useIdempotentMutation<
    PeriodCloseTask,
    { closeTaskId: string; waive: boolean; note: string }
  >({
    mutationFn: (input, key) =>
      decideCloseTask(input.closeTaskId, { waive: input.waive, note: input.note }, key),
    onSuccess: (_task, variables) => {
      setNotes((current) => ({ ...current, [variables.closeTaskId]: "" }));
      void queryClient.invalidateQueries({ queryKey: financeKeys.periodClose.all });
    },
    onError: (error) => surfaceProblem(error, t),
  });

  const transitionMutation = useIdempotentMutation<
    ClosePeriod,
    { fiscalPeriodId: string; target: ClosePeriodStatus; expected: ClosePeriodStatus }
  >({
    mutationFn: (input, key) =>
      transitionFiscalPeriodClose(
        input.fiscalPeriodId,
        { target_status: input.target, expected_status: input.expected },
        key,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: financeKeys.periodClose.all });
    },
    onError: (error) => surfaceProblem(error, t),
  });

  const reopenMutation = useIdempotentMutation<
    ClosePeriod,
    { fiscalPeriodId: string; body: PeriodReopen }
  >({
    mutationFn: (input, key) => reopenFiscalPeriod(input.fiscalPeriodId, input.body, key),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: financeKeys.periodClose.all });
    },
    onError: (error) => surfaceProblem(error, t),
  });

  const yearEndMutation = useIdempotentMutation<
    { status: number; close: YearEndClose | null },
    { fiscalYearId: string }
  >({
    mutationFn: (input, key) => executeYearEndClose({ fiscal_year_id: input.fiscalYearId }, key),
    onSuccess: (result) => {
      setYearEndResult(result);
      void queryClient.invalidateQueries({ queryKey: financeKeys.periodClose.all });
    },
    onError: (error) => surfaceProblem(error, t),
  });

  const decide = (task: PeriodCloseTask, waive: boolean) => {
    const note = (notes[task.id] ?? "").trim();
    if (waive && note === "") {
      setFormError(
        t(
          "finance.periodClose.waiverReasonRequired",
          "A waiver demands a reason in writing — without one nobody can find out why the close was allowed.",
        ),
      );
      return;
    }
    if (!waive && task.kind === "automated") {
      setFormError(
        t(
          "finance.periodClose.automatedNotSignable",
          "An automated task cannot be signed off. It is answered by its check.",
        ),
      );
      return;
    }
    setFormError(null);
    decideMutation.mutate({ closeTaskId: task.id, waive, note });
  };

  const forwardTarget =
    checklist === undefined ? undefined : FORWARD_TARGETS[checklist.period_status];

  return (
    <div className="flex flex-col gap-4 p-4">
      <PageHeader
        title={t("finance.periodClose.title", "Period close")}
        description={t(
          "finance.periodClose.subtitle",
          "The checklist, its evidence, and the one step the period may take next.",
        )}
      />

      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 pt-6">
          <SelectField
            id="close-period"
            label={t("finance.periodClose.period", "Fiscal period")}
            value={fiscalPeriodId}
            onValueChange={setFiscalPeriodId}
            className="w-80"
            options={periodOptions}
            disabled={!canReadFiscalPeriods}
          />
          {canRunChecks && fiscalPeriodId !== NONE ? (
            <Button
              type="button"
              variant="outline"
              loading={runChecksMutation.isPending}
              onClick={() => runChecksMutation.mutate({ fiscalPeriodId })}
            >
              <RefreshCw className="size-4" aria-hidden="true" />
              {t("finance.periodClose.runChecks", "Run every automated check")}
            </Button>
          ) : null}
        </CardContent>
      </Card>

      {checklist === undefined ? null : (
        <>
          <Card>
            <CardContent className="flex flex-col gap-3 pt-6">
              <div className="flex flex-wrap items-center gap-3">
                <StatusBadge
                  status={checklist.period_status}
                  label={t(
                    `finance.periodClose.periodStatus.${checklist.period_status}`,
                    checklist.period_status,
                  )}
                  variants={{ open: "outline", closing: "warning", closed: "success" }}
                />
                <span className="text-sm text-muted-foreground">
                  {checklist.closeable
                    ? t("finance.periodClose.closeable", "Nothing stands in the way.")
                    : t("finance.periodClose.blocked", "{{count}} tasks are still in the way.", {
                        count: checklist.blocking_task_codes.length,
                      })}
                </span>
              </div>
              {checklist.blocking_task_codes.length === 0 ? null : (
                <NoticeBanner>
                  {t("finance.periodClose.blockingCodes", "Blocking: {{codes}}", {
                    codes: checklist.blocking_task_codes.join(", "),
                  })}
                </NoticeBanner>
              )}
              <div className="flex flex-wrap items-end gap-3">
                {canTransition && forwardTarget !== undefined ? (
                  <Button
                    type="button"
                    disabled={!checklist.closeable}
                    loading={transitionMutation.isPending}
                    onClick={() =>
                      transitionMutation.mutate({
                        fiscalPeriodId: checklist.fiscal_period_id,
                        target: forwardTarget,
                        expected: checklist.period_status,
                      })
                    }
                  >
                    {t("finance.periodClose.transition", "Move to {{status}}", {
                      status: t(`finance.periodClose.periodStatus.${forwardTarget}`, forwardTarget),
                    })}
                  </Button>
                ) : null}
                {canReopen && checklist.period_status !== "open" ? (
                  <>
                    <SelectField
                      id="reopen-reason"
                      label={t("finance.periodClose.reopenReason", "Reopen reason")}
                      value={reopenReason}
                      onValueChange={(value) =>
                        setReopenReason(value as PeriodReopen["reason_code"])
                      }
                      className="w-72"
                      options={PERIOD_REOPEN_REASONS.map((value) => ({
                        value,
                        label: t(`finance.periodClose.reopenReasonOption.${value}`, value),
                      }))}
                      hint={t(
                        "finance.periodClose.reopenHint",
                        "A controlled reason code — the audit trail never accumulates free-form prose.",
                      )}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      loading={reopenMutation.isPending}
                      onClick={() =>
                        reopenMutation.mutate({
                          fiscalPeriodId: checklist.fiscal_period_id,
                          body: { target_status: "open", reason_code: reopenReason },
                        })
                      }
                    >
                      {t("finance.periodClose.reopen", "Reopen the period")}
                    </Button>
                  </>
                ) : null}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>
                <h2>{t("finance.periodClose.tasksSection", "Checklist")}</h2>
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              {checklist.tasks.map((task) => (
                <div key={task.id} className="flex flex-col gap-2 rounded-md border p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="flex flex-wrap items-center gap-2">
                      <StatusBadge
                        status={task.status}
                        label={t(`finance.periodClose.taskStatus.${task.status}`, task.status)}
                        variants={TASK_STATUS_VARIANTS}
                      />
                      <span className="font-medium">{task.name}</span>
                      <span dir="ltr" className="font-mono text-xs text-muted-foreground">
                        {task.code}
                      </span>
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {task.blocking
                        ? t("finance.periodClose.blockingTask", "Blocking")
                        : t("finance.periodClose.advisoryTask", "Advisory")}
                      {" · "}
                      {t(`finance.periodClose.taskKind.${task.kind}`, task.kind)}
                    </span>
                  </div>

                  {task.detail === "" ? null : <p className="text-sm">{task.detail}</p>}

                  {task.evidence.length === 0 ? null : (
                    <div className="text-sm">
                      <p className="text-muted-foreground">
                        {t(
                          "finance.periodClose.evidenceCount",
                          "{{shown}} rows shown of {{total}} found",
                          { shown: task.evidence.length, total: task.evidence_row_count },
                        )}
                      </p>
                      <ul className="mt-1 flex flex-col gap-1">
                        {task.evidence.map((row, index) => (
                          <li key={`${task.id}-${index}`} className="text-muted-foreground">
                            <span className="font-medium">{row.label}</span>{" "}
                            <span dir="ltr" className="font-mono text-xs">
                              {Object.entries(row.values)
                                .map(([key, value]) => `${key}=${value}`)
                                .join(" ")}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {canSignOff && task.status !== "signed_off" && task.status !== "waived" ? (
                    <div className="flex flex-wrap items-end gap-2">
                      <TextField
                        id={`task-note-${task.id}`}
                        label={t("finance.periodClose.note", "Note")}
                        value={notes[task.id] ?? ""}
                        onChange={(value) =>
                          setNotes((current) => ({ ...current, [task.id]: value }))
                        }
                        className="min-w-64 flex-1"
                        maxLength={2000}
                      />
                      {task.kind === "manual" ? (
                        <Button
                          type="button"
                          size="sm"
                          loading={decideMutation.isPending}
                          onClick={() => decide(task, false)}
                        >
                          {t("finance.periodClose.signOff", "Sign off")}
                        </Button>
                      ) : null}
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        loading={decideMutation.isPending}
                        onClick={() => decide(task, true)}
                      >
                        {t("finance.periodClose.waive", "Waive with a reason")}
                      </Button>
                    </div>
                  ) : null}
                </div>
              ))}
              {formError === null ? null : <ProblemBanner>{formError}</ProblemBanner>}
            </CardContent>
          </Card>
        </>
      )}

      <Card>
        <CardHeader>
          <CardTitle>
            <h2>{t("finance.periodClose.yearEndSection", "Year-end close")}</h2>
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <p className="text-sm text-muted-foreground">
            {t(
              "finance.periodClose.yearEndHint",
              "One journal closes every income and expense account into retained earnings. The trial balance is proved to net to zero before and after; the sweep happens once.",
            )}
          </p>
          <div className="flex flex-wrap items-end gap-3">
            <SelectField
              id="year-end-year"
              label={t("finance.periodClose.fiscalYear", "Fiscal year")}
              value={fiscalYearId}
              onValueChange={setFiscalYearId}
              className="w-72"
              options={yearOptions}
              disabled={!canReadFiscalYears}
            />
            {canExecuteYearEnd ? (
              <Button
                type="button"
                disabled={fiscalYearId === NONE}
                loading={yearEndMutation.isPending}
                onClick={() => yearEndMutation.mutate({ fiscalYearId })}
              >
                {t("finance.periodClose.executeYearEnd", "Sweep the year into retained earnings")}
              </Button>
            ) : null}
          </div>

          {yearEndResult !== null && yearEndResult.status === 202 ? (
            <NoticeBanner>
              {t(
                "finance.periodClose.yearEndApprovalRequired",
                "The close was routed for approval. Nothing posts until the decision lands.",
              )}
            </NoticeBanner>
          ) : null}

          {yearEndQuery.data === undefined ? (
            fiscalYearId === NONE ? null : (
              <p className="text-sm text-muted-foreground">
                {t("finance.periodClose.yearNotClosed", "This year has not been closed.")}
              </p>
            )
          ) : (
            <div className="rounded-md border p-3 text-sm" data-testid="year-end-close">
              <p>
                {t("finance.periodClose.retainedEarnings", "Moved to retained earnings")}{" "}
                <MoneyDisplay value={yearEndQuery.data.retained_earnings_amount} />
              </p>
              <p className="text-muted-foreground">
                {t(
                  "finance.periodClose.yearEndCounts",
                  "{{accounts}} accounts closed · {{openings}} opening balances seeded",
                  {
                    accounts: yearEndQuery.data.account_count,
                    openings: yearEndQuery.data.opening_balance_count,
                  },
                )}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
